import { Container, FillGradient, Graphics, Rectangle, Text } from 'pixi.js';
import { createSpineAdapter } from '../animation/createSpineAdapter.js';
import { gameConfig } from '../config/gameConfig.js';
import { formatCurrencyDigits, formatCurrencyRulesLabel } from '../lib/formatCurrency.js';
import { isPrimaryPointerAction } from '../utils/pointerPrimary.js';

export class UILayer {
  /**
   * @param {{
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   *   onSpin: () => void,
   *   translate?: (key: string, replace?: Record<string, string>) => string,
   *   panelWidth: number,
   *   panelHeight: number,
   *   onBetLayoutChanged?: () => void,
   *   onBetSync?: (value: number) => void,
   *   onLinesSync?: (value: number) => void,
   *   onAutoplayToggle?: (active: boolean) => void,
   *   onMaxBet?: () => void,
   *   onSettingsToggle?: () => void,
   * }} opts
   */
  constructor(opts) {
    this.animationController = opts.animationController;
    this.onSpin = opts.onSpin;
    this.panelWidth = opts.panelWidth;
    this.panelHeight = opts.panelHeight;
    this.onBetLayoutChanged = opts.onBetLayoutChanged;
    this.onBetSync = opts.onBetSync;
    this.onLinesSync = opts.onLinesSync;
    this.onAutoplayToggle = opts.onAutoplayToggle;
    this.onMaxBet = opts.onMaxBet;
    this.onSettingsToggle = opts.onSettingsToggle;
    this.onBetPopupToggle = opts.onBetPopupToggle;
    this.translate = typeof opts.translate === 'function' ? opts.translate : (key) => key;
    this.currencyCode = 'FUN';
    this.currencyPrecision = 2;
    this.currencySymbol = undefined;
    this.t = gameConfig.theme;
    this.u = gameConfig.ui;

    this.root = new Container();
    /** @type {{ x: number, y: number, w: number, h: number } | null} */
    this._betInputRect = null;
    /** @type {{ x: number, y: number, w: number, h: number } | null} */
    this._linesInputRect = null;
    /** Extra hit-area padding (logical px) on coarse / small viewports */
    this._touchPad = 0;
    this._layoutMode = 'desktop-landscape';
    this._defaultPanelH = opts.panelHeight;
    this._spinBaseScale = 1;
    this._spinVisualW = this.u.spinW;
    this._spinVisualH = this.u.spinH;
    this._betCardY = this.u.panelPadY;
    this._betCardH = this.u.statRowH;
    /** Logical-per-screen-pixel ratio (=1 on desktop; >1 when world is scaled down on phones). */
    this._uiMul = 1;
    /** Actual CSS pixel size of the canvas — used to pick comfortable touch sizes on mobile. */
    this._cssW = 0;
    this._cssH = 0;
    this._autoplaying = false;

    /** Horizontal scroll state for mobile-portrait preset chip row. */
    this._chipScrollX = 0;
    this._chipScrollMaxX = 0;
    this._chipScrollDidDrag = false;
    this._chipScrollDragging = false;
    this._chipScrollStartGlobalX = 0;
    this._chipScrollStartScrollX = 0;
    /** Fixed chip item width set during layout (px in panel logical space). */
    this._chipItemW = 60;

    this.balanceValue = null;
    this.betValue = gameConfig.defaultBet;
    /** Active paylines: first N entries in {@link gameConfig.paylines} order (1–9). */
    this.linesValue = 1;
    this.winValue = 0;
    /** @type {import('../logic/winEvaluator.js').WinResult['hits']} */
    this._winHits = [];
    /** Preset bet amounts shown as chips in mobile portrait (populated via setQuickBets). */
    this._quickBets = [];
    /** @type {import('pixi.js').Container[]} */
    this._presetChipContainers = [];

    this._drawPanel();
    this._buildStatBlocks();
    this._buildBetControls();
    this._buildChipsScrollGroup();
    this._hideLinesUi();
    this._layoutAllBetLikeRows();
    this._buildSpinButton();
    this._buildAutoplayBtn();
    this._buildMaxBetBtn();
    this._buildSettingsBtn();
    this._buildInfoText();
    this._buildAutoCircleBtn();
    this._buildPaytableBtn();
    this.setSpinEnabled(true);

    /* betText is now rendered directly (no HTML input overlay) */
    this.linesText.renderable = false;
  }

  /** Width reserved for SPIN + gap before it (panel side padding is separate). */
  _spinBlockWidth() {
    return this.u.spinW + this.u.spinGap;
  }

  _drawPanel() {
    const w = this.panelWidth;
    const h = this.panelHeight;
    const r = this.u.radiusPanel;
    const g = new Graphics();
    const grad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: this.t.panelRimHi },
        { offset: 0.08, color: this.t.panelGlass },
        { offset: 0.22, color: this.t.panelMid },
        { offset: 0.58, color: this.t.panelDeep },
        { offset: 1, color: this.t.panelShadow },
      ],
    });
    g.roundRect(0, 0, w, h, r).fill(grad);
    g.roundRect(0, 0, w, h, r).stroke({
      width: this.u.strokePanelOuter,
      color: this.t.panelBorder,
      alpha: 0.55,
    });
    const ir = Math.max(0, r - 3);
    g.roundRect(3, 3, w - 6, h - 6, ir).stroke({
      width: this.u.strokePanelInner,
      color: this.t.skyGlow,
      alpha: 0.22,
    });
    g.moveTo(12, 2).lineTo(w - 12, 2).stroke({
      width: 1,
      color: 0xffffff,
      alpha: 0.12,
    });
    g.eventMode = 'none';
    this._panelBg = g;
    this.root.addChild(g);
  }

  _repaintPanelBg() {
    const w = this.panelWidth;
    const h = this.panelHeight;
    const r = this.u.radiusPanel;
    const g = this._panelBg;
    if (!g) return;
    const grad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: this.t.panelRimHi },
        { offset: 0.08, color: this.t.panelGlass },
        { offset: 0.22, color: this.t.panelMid },
        { offset: 0.58, color: this.t.panelDeep },
        { offset: 1, color: this.t.panelShadow },
      ],
    });
    g.clear();
    g.roundRect(0, 0, w, h, r).fill(grad);
    g.roundRect(0, 0, w, h, r).stroke({
      width: this.u.strokePanelOuter,
      color: this.t.panelBorder,
      alpha: 0.55,
    });
    const ir = Math.max(0, r - 3);
    g.roundRect(3, 3, w - 6, h - 6, ir).stroke({
      width: this.u.strokePanelInner,
      color: this.t.skyGlow,
      alpha: 0.22,
    });
    g.moveTo(12, 2).lineTo(w - 12, 2).stroke({
      width: 1,
      color: 0xffffff,
      alpha: 0.12,
    });
  }

  /**
   * Resize bottom bar to full viewport width (logical px).
   * @param {number} w
   */
  setPanelWidth(w, force = false) {
    if (w < 2) return;
    const rw = Math.round(w);
    if (!force && rw === Math.round(this.panelWidth)) return;
    this.panelWidth = rw;
    this._repaintPanelBg();

    const mul = this._uiMul || 1;
    const inset = Math.round(this.u.panelPadX * mul);
    const top = Math.round(this.u.panelPadY * mul);
    const cardH = Math.round(this.u.statRowH * mul);
    const gap = Math.round(this.u.cardGap * mul);
    const cardPadX = Math.round(this.u.cardPadX * mul);
    const cardPadY = Math.round(this.u.cardPadY * mul);
    const isMobilePortrait = this._layoutMode === 'mobile-portrait';
    const isTabletPortrait = this._layoutMode === 'tablet-portrait';
    const isMobileLandscape = this._layoutMode === 'mobile-landscape';
    const applyFontScales = () => {
      const statTrack = (this.u.statValueTracking ?? 0) * mul;
      this.balanceText.style.fontSize = Math.round(this.u.typeStatValue * mul);
      this.balanceText.style.letterSpacing = statTrack;
      if (this.totalBetText) {
        this.totalBetText.style.fontSize = Math.round(this.u.typeStatValue * mul);
        this.totalBetText.style.letterSpacing = statTrack;
      }
      this.betText.style.fontSize = Math.round(this.u.typeStatValue * mul);
      this.winText.style.fontSize = Math.round(this.u.typeWinLastTotal * mul);
      this._labelBalance.style.fontSize = Math.round(this.u.typeStatLabel * mul);
      if (this._labelTotalBet) {
        this._labelTotalBet.style.fontSize = Math.round(this.u.typeStatLabel * mul);
      }
      this._betStatLabel.style.fontSize = Math.round(this.u.typeStatLabel * mul);
      this._linesStatLabel.style.fontSize = Math.round(this.u.typeStatLabel * mul);
      this._labelWin.style.fontSize = Math.round(this.u.typeStatLabel * mul);
      this.winDetailText.style.fontSize = Math.round(this.u.typeWinDetail * mul);
      this.winDetailText.style.lineHeight = Math.round(this.u.typeWinDetail * mul * 1.4);
      this._syncChromeShadows(mul);
    };
    applyFontScales();
    if (isMobilePortrait) {
      /* ═══ Mobile portrait: Shining Crown 3-row structure ═══
       *  Row 1: BALANCE (left, stacked) | INFO TEXT (centre) | LAST WIN (right)
       *  Row 2: Preset bet chips spread full width
       *  Row 3: AUTO circle | SPIN circle (large) | PAYTABLE circle
       */

      /* Show portrait-specific elements; hide landscape ones */
      if (this._autoCircleBtn) { this._autoCircleBtn.visible = true; this._autoCircleBtn.eventMode = 'static'; }
      if (this._paytableBtn)   { this._paytableBtn.visible   = true; this._paytableBtn.eventMode   = 'static'; }
      if (this._infoText)      this._infoText.visible = true;
      if (this._currencyBadge) this._currencyBadge.visible = true;
      if (this._autoBtn)         { this._autoBtn.visible         = false; this._autoBtn.eventMode         = 'none'; }
      if (this._settingsIconBtn) { this._settingsIconBtn.visible = false; this._settingsIconBtn.eventMode = 'none'; }
      if (this._maxBetBtn)       this._maxBetBtn.visible = false;
      if (this._betChipMinus)  { this._betChipMinus.visible  = false; this._betChipMinus.eventMode  = 'none'; }
      if (this._betChipPlus)   { this._betChipPlus.visible   = false; this._betChipPlus.eventMode   = 'none'; }
      if (this._betStatLabel)  this._betStatLabel.visible  = false;

      const edge     = Math.max(10 * mul, inset - 10 * mul);
      const topRowH  = Math.max(52 * mul, cardH - 4 * mul);
      const rowGap   = Math.max(8  * mul, gap  - 2 * mul);
      const chipH    = Math.round(this.u.betChipH  * mul);
      const chipPadY = Math.round(this.u.cardPadY  * mul);
      const chipRowH = chipH + chipPadY * 2;

      /* Y co-ordinates */
      const linesY   = top + topRowH + rowGap;
      const spinD    = this._spinVisualW;
      const spinRowY = linesY + chipRowH + rowGap;
      const spinX    = Math.round((w - spinD) / 2);

      /* ── ROW 2: scrollable preset bet chips ── */
      if (this._chipsScrollGroup) {
        this._chipsScrollGroup.visible = true;
        this._chipsScrollGroup.eventMode = 'static';
      }

      /* ── SPIN (centre) ── */
      this.spinButton.x = spinX;
      this.spinButton.y = spinRowY;
      this._spinBaseScale = 1;
      this.spinButton.scale.set(this._spinBaseScale);

      /* ── AUTO circle (left of spin) ── */
      const aD      = Math.round((this._autoCircleBtnD ?? 54) * mul);
      const sideGap = Math.max(Math.round(8 * mul), Math.floor((spinX - aD - edge) / 2));
      if (this._autoCircleBtn) {
        this._autoCircleBtn.scale.set(mul);
        this._autoCircleBtn.x = Math.max(edge, spinX - aD - sideGap);
        this._autoCircleBtn.y = spinRowY + Math.round((spinD - aD) / 2);
      }

      /* ── PAYTABLE circle (right of spin) ── */
      const pD = Math.round((this._paytableBtnD ?? 54) * mul);
      if (this._paytableBtn) {
        this._paytableBtn.scale.set(mul);
        this._paytableBtn.x = Math.min(w - pD - edge, spinX + spinD + sideGap);
        this._paytableBtn.y = spinRowY + Math.round((spinD - pD) / 2);
      }

      /* ── ROW 1: three equal columns ── */
      const colGap = Math.max(Math.round(6 * mul), rowGap);
      const colW   = Math.max(60 * mul, Math.floor((w - edge * 2 - colGap * 2) / 3));
      const balX   = edge;
      const infoColX = balX + colW + colGap;
      const winX   = infoColX + colW + colGap;

      this._repaintStatCardGfx(this._cardBalanceG, colW, topRowH);
      this._repaintStatCardGfx(this._cardWinG, colW, topRowH);

      this._cardBalanceG.x  = balX; this._cardBalanceG.y  = top;
      this._balanceCardX    = balX; this._balanceCardY    = top;
      this._balanceCardW    = colW; this._balanceCardH    = topRowH;
      this._labelBalance.x  = balX + cardPadX;
      this._labelBalance.y  = top  + cardPadY;

      this._cardWinG.x   = winX; this._cardWinG.y   = top;
      this._labelWin.x   = winX + cardPadX;
      this._labelWin.y   = top  + cardPadY;
      this._winCard      = { x: winX, y: top, w: colW, h: topRowH };

      /* Info text centred in middle column */
      if (this._infoText) {
        this._infoText.style.fontSize = Math.round(10 * mul);
        this._infoText.style.wordWrapWidth = colW - 2 * cardPadX;
        this._infoText.x = infoColX + colW / 2;
        this._infoText.y = top + topRowH / 2;
      }

      /* ── ROW 2: scrollable preset bet chips (3 visible, rest scroll) ── */
      const nChips = this._presetChipContainers.length;
      if (nChips > 0 && this._chipsScrollGroup) {
        const rowW        = w - edge * 2;
        const chipGapPx   = Math.round(6 * mul);
        /* Show 3 fully + partial peek at 4th to signal scrollability */
        const visibleCols = Math.min(3.3, nChips);
        const chipW       = Math.max(28, Math.floor((rowW - chipGapPx * (Math.ceil(visibleCols) - 1)) / Math.ceil(visibleCols)));
        const totalContentW = chipW * nChips + chipGapPx * (nChips - 1);
        this._chipItemW   = chipW;
        this._chipScrollMaxX = nChips > Math.ceil(visibleCols)
          ? Math.min(0, -(totalContentW - rowW))
          : 0;
        this._chipScrollX = Math.max(this._chipScrollMaxX, Math.min(0, this._chipScrollX));

        /* Update clip mask (in root/panel local space) */
        this._chipsScrollGroupMask.clear();
        this._chipsScrollGroupMask.rect(edge, linesY + chipPadY, rowW, chipH).fill({ color: 0xffffff });

        /* Position the scroll container and expand its hit area across the full row. */
        this._chipsScrollGroup.x = edge;
        this._chipsScrollGroup.y = linesY + chipPadY;
        this._chipsScrollGroup.hitArea = new Rectangle(0, 0, rowW, chipH);

        this._presetChipContainers.forEach((c, i) => {
          c.visible   = true;
          c.eventMode = 'static';
          c.scale.set(1);
          c.x = i * (chipW + chipGapPx) + this._chipScrollX;
          c.y = 0;
          c.hitArea = new Rectangle(0, 0, chipW, chipH);
          const sel = Math.abs((c._betAmount ?? -Infinity) - this.betValue) < 0.001;
          this._paintPresetChipBg(c._chipBg, sel, chipW, chipH);
          if (c._amtText)  { c._amtText.x  = chipW / 2; c._amtText.y  = chipH * 0.64; }
          if (c._betLabel) { c._betLabel.x = chipW / 2; }
        });
      }

      /* Store bet-card bounds (used by layout helpers) */
      this._betCardX = balX;     this._betCardY = linesY;
      this._betCardW = w - edge * 2; this._betCardH = chipRowH;
      this._linesCardX = balX;   this._linesCardY = linesY;
      this._linesCardW = colW;   this._linesCardH = chipRowH;
      this.winDetailText.style.wordWrapWidth = Math.max(40, colW - 2 * cardPadX);
    } else if (isTabletPortrait) {
      /* Hide portrait-only elements */
      if (this._chipsScrollGroup) { this._chipsScrollGroup.visible = false; this._chipsScrollGroup.eventMode = 'none'; }
      if (this._autoCircleBtn) { this._autoCircleBtn.visible = false; this._autoCircleBtn.eventMode = 'none'; }
      if (this._paytableBtn)   { this._paytableBtn.visible   = false; this._paytableBtn.eventMode   = 'none'; }
      if (this._infoText)      this._infoText.visible      = false;
      if (this._currencyBadge) this._currencyBadge.visible = false;
      if (this._betChipMinus) { this._betChipMinus.visible = true; this._betChipMinus.eventMode = 'static'; }
      if (this._betChipPlus)  { this._betChipPlus.visible  = true; this._betChipPlus.eventMode  = 'static'; }
      if (this._betStatLabel) this._betStatLabel.visible = true;
      const edge = Math.max(14 * mul, inset - 6 * mul);
      const topRowH = Math.max(64 * mul, cardH - 4 * mul);
      const rowGap = gap;
      const cardW = Math.max(100 * mul, Math.floor((w - edge * 2 - gap * 3) / 4));
      const balX = edge;
      const linesX = balX + cardW + gap;
      const betX = linesX + cardW + gap;
      const winX = betX + cardW + gap;
      const row2Y = top + topRowH + rowGap;
      const spinScale = 1;

      this._linesCardX = linesX;
      this._linesCardY = top;
      this._linesCardW = cardW;
      this._linesCardH = topRowH;

      this._betCardX = betX;
      this._betCardY = top;
      this._betCardW = cardW;
      this._betCardH = topRowH;
      this._winCard = { x: winX, y: top, w: cardW, h: topRowH };

      this._repaintStatCardGfx(this._cardBalanceG, cardW, topRowH);
      this._repaintStatCardGfx(this._cardLinesG, cardW, topRowH);
      this._repaintStatCardGfx(this._cardBetG, cardW, topRowH);
      this._repaintStatCardGfx(this._cardWinG, cardW, topRowH);

      this._cardBalanceG.x = balX;
      this._cardBalanceG.y = top;
      this._balanceCardX = balX;
      this._balanceCardY = top;
      this._balanceCardW = cardW;
      this._balanceCardH = topRowH;
      this._labelBalance.x = balX + cardPadX;
      this._labelBalance.y = top + cardPadY;

      this._cardLinesG.x = linesX;
      this._cardLinesG.y = top;
      this._linesStatLabel.x = linesX + cardPadX;
      this._linesStatLabel.y = top + cardPadY;
      this.linesText.x = linesX + cardPadX;
      this.linesText.y = top + topRowH - cardPadY;

      this._cardBetG.x = betX;
      this._cardBetG.y = top;
      this._betStatLabel.x = betX + cardPadX;
      this._betStatLabel.y = top + cardPadY;
      this.betText.x = betX + cardPadX;
      this.betText.y = top + topRowH - cardPadY;

      this._cardWinG.x = winX;
      this._cardWinG.y = top;
      this._labelWin.x = winX + cardPadX;
      this._labelWin.y = top + cardPadY;
      this.winDetailText.style.wordWrapWidth = Math.max(40, cardW - 2 * cardPadX);

      this._spinBaseScale = spinScale;
      this.spinButton.scale.set(this._spinBaseScale);
      this.spinButton.x = Math.round((w - this._spinVisualW * spinScale) / 2);
      this.spinButton.y = row2Y;

      /* Tablet portrait: AUTO and settings left of spin, MAX hidden */
      const tSpinX = this.spinButton.x;
      const aSc = Math.min(mul, 1);
      const tGap = 10 * aSc;
      if (this._autoBtn) {
        this._autoBtn.visible = true;
        this._autoBtn.scale.set(aSc);
        this._autoBtn.x = tSpinX - (this._autoBtnW ?? 80) * aSc - tGap;
        this._autoBtn.y = row2Y + (this._spinVisualH * spinScale - (this._autoBtnH ?? 44) * aSc) / 2;
      }
      if (this._settingsIconBtn) {
        this._settingsIconBtn.visible = true;
        this._settingsIconBtn.scale.set(aSc);
        this._settingsIconBtn.x = (this._autoBtn?.x ?? tSpinX) - (this._settingsBtnW ?? 44) * aSc - tGap;
        this._settingsIconBtn.y = row2Y + (this._spinVisualH * spinScale - (this._settingsBtnH ?? 44) * aSc) / 2;
      }
      if (this._maxBetBtn) this._maxBetBtn.visible = false;

      /* Center bet value in its card for tablet portrait */
      this.betText.anchor.set(0.5, 0.5);
      this.betText.x = betX + cardW / 2;
      this.betText.y = top + topRowH / 2;
    } else {
      /* Hide portrait-only elements */
      if (this._chipsScrollGroup) { this._chipsScrollGroup.visible = false; this._chipsScrollGroup.eventMode = 'none'; }
      if (this._autoCircleBtn) { this._autoCircleBtn.visible = false; this._autoCircleBtn.eventMode = 'none'; }
      if (this._paytableBtn)   { this._paytableBtn.visible   = false; this._paytableBtn.eventMode   = 'none'; }
      if (this._infoText)      this._infoText.visible      = false;
      if (this._currencyBadge) this._currencyBadge.visible = false;
      if (this._betChipMinus) { this._betChipMinus.visible = true; this._betChipMinus.eventMode = 'static'; }
      if (this._betChipPlus)  { this._betChipPlus.visible  = true; this._betChipPlus.eventMode  = 'static'; }
      if (this._betStatLabel) this._betStatLabel.visible = true;

      const spinW = this._spinVisualW;
      const spinH = this._spinVisualH;
      const spinGap = Math.round(this.u.spinGap * mul);
      const spinBlock = spinW + spinGap;
      const inner = w - inset * 2 - spinBlock;
      const cardW = Math.max((isMobileLandscape ? 80 : 96) * mul, Math.floor((inner - gap * 3) / 4));
      const linesX = inset + cardW + gap;
      const betX = linesX + cardW + gap;
      const winX = betX + cardW + gap;
      const winW = cardW;

      this._linesCardX = linesX;
      this._linesCardY = top;
      this._linesCardW = cardW;
      this._linesCardH = cardH;

      this._betCardX = betX;
      this._betCardY = top;
      this._betCardW = cardW;
      this._betCardH = cardH;
      this._winCard = { x: winX, y: top, w: winW, h: cardH };

      this._repaintStatCardGfx(this._cardBalanceG, cardW, cardH);
      this._repaintStatCardGfx(this._cardLinesG, cardW, cardH);
      this._repaintStatCardGfx(this._cardBetG, cardW, cardH);
      this._repaintStatCardGfx(this._cardWinG, winW, cardH);

      this._cardBalanceG.x = inset;
      this._cardBalanceG.y = top;
      this._balanceCardX = inset;
      this._balanceCardY = top;
      this._balanceCardW = cardW;
      this._balanceCardH = cardH;
      this._labelBalance.x = inset + cardPadX;
      this._labelBalance.y = top + cardPadY;

      this._cardLinesG.x = linesX;
      this._cardLinesG.y = top;
      this._linesStatLabel.x = linesX + cardPadX;
      this._linesStatLabel.y = top + cardPadY;
      this.linesText.x = linesX + cardPadX;
      this.linesText.y = top + cardH - cardPadY;

      this._cardBetG.x = betX;
      this._cardBetG.y = top;
      this._betStatLabel.x = betX + cardPadX;
      this._betStatLabel.y = top + cardPadY;
      this.betText.x = betX + cardPadX;
      this.betText.y = top + cardH - cardPadY;

      this._cardWinG.x = winX;
      this._cardWinG.y = top;
      this._labelWin.x = winX + cardPadX;
      this._labelWin.y = top + cardPadY;

      this.winDetailText.style.wordWrapWidth = Math.max(40, winW - 2 * cardPadX);

      const px = w - spinW - inset;
      const py = isMobileLandscape ? top + cardH + 2 * mul : top + (cardH - spinH) / 2;
      this.spinButton.x = px;
      this.spinButton.y = py;
      this._spinBaseScale = isMobileLandscape ? 0.96 : 1;
      this.spinButton.scale.set(this._spinBaseScale);

      /* AUTO button: left of SPIN */
      const aBtnW = this._autoBtnW ?? 80;
      const aBtnH = this._autoBtnH ?? 44;
      const aScale = Math.min(mul, 1);
      const aGap = Math.round(spinGap * 0.6);
      if (this._autoBtn) {
        this._autoBtn.visible = true;
        this._autoBtn.scale.set(aScale);
        this._autoBtn.x = px - aBtnW * aScale - aGap;
        this._autoBtn.y = py + (spinH - aBtnH * aScale) / 2;
      }
      /* Settings: left of AUTO */
      const sW = this._settingsBtnW ?? 44;
      if (this._settingsIconBtn) {
        this._settingsIconBtn.visible = true;
        this._settingsIconBtn.scale.set(aScale);
        this._settingsIconBtn.x = (this._autoBtn?.x ?? px) - sW * aScale - aGap;
        this._settingsIconBtn.y = py + (spinH - (this._settingsBtnH ?? 44) * aScale) / 2;
      }
      /* MAX: hidden on landscape (no room; BET+ serves the purpose) */
      if (this._maxBetBtn) this._maxBetBtn.visible = false;

      /* Align bet text centered in its card */
      this.betText.anchor.set(0.5, 0.5);
      this.betText.x = betX + cardW / 2;
      this.betText.y = top + cardH / 2;
    }

    this._layoutAllBetLikeRows();
    this._layoutInlineBalanceAndTotalBet();
    this._layoutWinCardTexts();
    this._hideLinesUi();
  }

  /** Balance amount — stacked in mobile portrait, inline in all other modes. */
  _layoutInlineBalanceAndTotalBet() {
    if (!this._labelBalance || !this.balanceText) return;
    const mul = this._uiMul || 1;
    const cardPadX = Math.round(this.u.cardPadX * mul);
    const cardPadY = Math.round(this.u.cardPadY * mul);
    const colX = this._balanceCardX != null ? this._balanceCardX + cardPadX : this._labelBalance.x;

    if (this._layoutMode === 'mobile-portrait') {
      /* Stacked layout: BALANCE: label → value → FUN badge */
      this.balanceText.anchor.set(0, 0);
      this._labelBalance.x = colX;
      this._labelBalance.y = this._balanceCardY != null
        ? this._balanceCardY + cardPadY
        : this._labelBalance.y;
      this.balanceText.x = colX;
      this.balanceText.y = this._labelBalance.y + this._labelBalance.height + 2;
      if (this._currencyBadge) {
        this._currencyBadge.anchor?.set(0, 0);
        this._currencyBadge.x = colX;
        this._currencyBadge.y = this.balanceText.y + this.balanceText.height + 3;
        this._currencyBadge.visible = true;
      }
      return;
    }

    /* ── Inline layout (desktop / landscape / tablet) ── */
    const inlineGap = Math.max(8, Math.round((this.u.statInlineGap ?? 14) * mul));
    const stackGap  = Math.max(3, Math.round((this.u.statStackGap  ?? 5)  * mul));
    if (this._currencyBadge) this._currencyBadge.visible = false;

    this.balanceText.anchor.set(0, 0);
    this._labelBalance.x = colX;

    const row1H = Math.max(this._labelBalance.height, this.balanceText.height);
    const hasTotal =
      this._labelTotalBet &&
      this.totalBetText &&
      this._balanceCardX != null &&
      this._balanceCardW != null &&
      this._balanceCardH != null;

    const pairH       = hasTotal ? Math.max(this._labelTotalBet.height, this.totalBetText.height) : 0;
    const totalBlockH = hasTotal ? row1H + stackGap + pairH : row1H;
    const availableH  = this._balanceCardH != null
      ? Math.max(0, this._balanceCardH - 2 * cardPadY)
      : totalBlockH;

    let startY;
    if (this._balanceCardY != null && this._balanceCardH != null) {
      startY = this._balanceCardY + cardPadY + Math.max(0, (availableH - totalBlockH) / 2);
    } else if (this._balanceCardY != null) {
      startY = this._balanceCardY + cardPadY;
    } else {
      startY = this._labelBalance.y;
    }

    this._labelBalance.y = startY + (row1H - this._labelBalance.height) / 2;
    this.balanceText.x   = this._labelBalance.x + this._labelBalance.width + inlineGap;
    this.balanceText.y   = startY + (row1H - this.balanceText.height) / 2;

    if (!hasTotal) return;
    this._labelTotalBet.anchor.set(0, 0);
    this.totalBetText.anchor.set(0, 0);
    const row2Top = startY + row1H + stackGap;
    this._labelTotalBet.x = colX;
    this._labelTotalBet.y = row2Top + (pairH - this._labelTotalBet.height) / 2;
    this.totalBetText.x   = this._labelTotalBet.x + this._labelTotalBet.width + inlineGap;
    this.totalBetText.y   = row2Top + (pairH - this.totalBetText.height) / 2;
  }

  setPanelHeight(h) {
    const rh = Math.max(2, Math.round(h));
    if (rh === Math.round(this.panelHeight)) return;
    this.panelHeight = rh;
    this._repaintPanelBg();
    this.setPanelWidth(this.panelWidth, true);
  }

  _computePanelHeight() {
    const mul = this._uiMul || 1;
    const mode = this._layoutMode;
    if (mode === 'mobile-portrait') {
      /** Row 1: stats | Row 2: bet controls | Row 3: [⚙][AUTO][SPIN][MAX] */
      const inset = this.u.panelPadY * 2;
      const topRowH = Math.max(52, this.u.statRowH - 4);
      const betRowH = Math.max(52, this.u.betChipH + this.u.cardPadY * 2);
      const rowGap = Math.max(8, this.u.cardGap - 2);
      const spinD = 110;
      const bottomPad = 12;
      return Math.round((inset + topRowH + rowGap + betRowH + rowGap + spinD + bottomPad) * mul);
    }
    if (mode === 'tablet-portrait') {
      return Math.round(this._defaultPanelH * 1.85 * mul);
    }
    if (mode === 'mobile-landscape') {
      return Math.round(this._defaultPanelH * 1.06 * mul);
    }
    return Math.round(this._defaultPanelH * mul);
  }

  setLayoutMode(mode) {
    const allowed = new Set([
      'desktop-landscape',
      'desktop-portrait',
      'tablet-landscape',
      'tablet-portrait',
      'mobile-landscape',
      'mobile-portrait',
    ]);
    const next = allowed.has(mode) ? mode : 'desktop-landscape';
    if (next === this._layoutMode) return;
    this._layoutMode = next;
    this._repaintSpinButton();
    this.setPanelHeight(this._computePanelHeight());
  }

  /**
   * Widen SPIN / bet-chip pointer targets on phones and small tablets (CSS px).
   * @param {number} cssW
   * @param {number} cssH
   */
  setResponsiveViewport(cssW, cssH) {
    const short = Math.min(cssW, cssH);
    const comfort = cssW < 580 || short < 540;
    const pad = comfort ? 14 : 0;
    const prevMul = this._uiMul;
    this._cssW = cssW;
    this._cssH = cssH;
    /** Keep UI chrome rendering at screen-natural sizes, even when cabinet dominates scale. */
    const nextMul = cssW > 0
      ? Math.max(1, Math.min(3.5, this.panelWidth / cssW))
      : 1;
    this._uiMul = nextMul;
    const mulChanged = Math.abs(nextMul - prevMul) > 0.02;
    const padChanged = pad !== this._touchPad;
    this._touchPad = pad;
    if (mulChanged) {
      this._repaintSpinButton();
      this.setPanelHeight(this._computePanelHeight());
      this.setPanelWidth(this.panelWidth, true);
    } else if (padChanged) {
      this._refreshPointerHitAreas();
    }
  }

  _syncChromeShadows(mul) {
    const lb = Math.max(1.5, 2.2 * mul);
    const ld = Math.max(1, Math.round(mul));
    const labelSh = {
      alpha: 0.52,
      angle: Math.PI / 2,
      blur: lb,
      color: 0x000000,
      distance: ld,
    };
    for (const node of [
      this._labelBalance,
      this._labelTotalBet,
      this._betStatLabel,
      this._linesStatLabel,
      this._labelWin,
    ]) {
      if (node) node.style.dropShadow = { ...labelSh };
    }
    if (this.balanceText) {
      this.balanceText.style.dropShadow = {
        alpha: 0.62,
        angle: Math.PI / 2,
        blur: Math.max(2.5, 4 * mul),
        color: this.t.balanceGlow,
        distance: 0,
      };
    }
    if (this.totalBetText) {
      this.totalBetText.style.dropShadow = {
        alpha: 0.72,
        angle: Math.PI / 2,
        blur: Math.max(2.5, 4.8 * mul),
        color: this.t.goldDim,
        distance: 0,
      };
    }
  }

  _refreshPointerHitAreas() {
    const p = this._touchPad;
    const sw = this._spinVisualW * (this._spinBaseScale || 1);
    const sh = this._spinVisualH * (this._spinBaseScale || 1);
    if (this.spinButton) {
      this.spinButton.hitArea = new Rectangle(-p, -p, sw + 2 * p, sh + 2 * p);
    }
    const bw = this.u.betChipW;
    const bh = this.u.betChipH;
    if (this._betChipMinus) {
      this._betChipMinus.hitArea = new Rectangle(-p, -p, bw + 2 * p, bh + 2 * p);
    }
    if (this._betChipPlus) {
      this._betChipPlus.hitArea = new Rectangle(-p, -p, bw + 2 * p, bh + 2 * p);
    }
    if (this._linesChipMinus) {
      this._linesChipMinus.hitArea = new Rectangle(-p, -p, bw + 2 * p, bh + 2 * p);
    }
    if (this._linesChipPlus) {
      this._linesChipPlus.hitArea = new Rectangle(-p, -p, bw + 2 * p, bh + 2 * p);
    }
    const aw = this._autoBtnW ?? 80;
    const ah = this._autoBtnH ?? 44;
    if (this._autoBtn) {
      this._autoBtn.hitArea = new Rectangle(-p, -p, aw + 2 * p, ah + 2 * p);
    }
    if (this._maxBetBtn) {
      this._maxBetBtn.hitArea = new Rectangle(-p, -p, (this._maxBtnW ?? 80) + 2 * p, (this._maxBtnH ?? 44) + 2 * p);
    }
    if (this._settingsIconBtn) {
      this._settingsIconBtn.hitArea = new Rectangle(-p, -p, (this._settingsBtnW ?? 44) + 2 * p, (this._settingsBtnH ?? 44) + 2 * p);
    }
  }

  /** Stat “cards” are layout-only — no boxed sections; chrome is the panel beneath. */
  _repaintStatCardGfx(g, _bw, _bh) {
    if (!g) return;
    g.clear();
  }

  _statCard(x, y, bw, bh, label, valueText, _balanceHero = false) {
    const px = this.u.cardPadX;
    const py = this.u.cardPadY;
    const g = new Graphics();
    g.eventMode = 'none';
    g.visible = false;
    g.x = x;
    g.y = y;
    this.root.addChild(g);

    const lab = new Text({
      text: label,
      style: {
        fontFamily: this.t.fontUi,
        fontSize: this.u.typeStatLabel,
        fontWeight: '600',
        fill: this.t.textLabel,
        letterSpacing: 1.2,
        dropShadow: {
          alpha: 0.4,
          angle: Math.PI / 2,
          blur: 2,
          color: 0x000000,
          distance: 1,
        },
      },
    });
    lab.x = x + px;
    lab.y = y + py;
    lab.eventMode = 'none';
    this.root.addChild(lab);

    valueText.anchor.set(0, 1);
    valueText.x = x + px;
    valueText.y = y + bh - py;
    valueText.eventMode = 'none';
    this.root.addChild(valueText);
    return { g, label: lab };
  }

  _buildStatBlocks() {
    const w = this.panelWidth;
    const inset = this.u.panelPadX;
    const top = this.u.panelPadY;
    const cardH = this.u.statRowH;
    const spinBlock = this._spinBlockWidth();
    const gap = this.u.cardGap;
    const inner = w - inset * 2 - spinBlock;
    const cardW = Math.max(96, Math.floor((inner - gap * 3) / 4));

    this.balanceText = new Text({
      text: '',
      style: {
        fontFamily: this.t.fontUi,
        fontSize: this.u.typeStatValue,
        fontWeight: '800',
        fill: this.t.balanceFigure,
        letterSpacing: this.u.statValueTracking ?? 0,
        dropShadow: {
          alpha: 0.62,
          angle: Math.PI / 2,
          blur: 4,
          color: this.t.balanceGlow,
          distance: 0,
        },
      },
    });
    const bal = this._statCard(inset, top, cardW, cardH, this._txtUpper('BALANCE'), this.balanceText, true);
    this._cardBalanceG = bal.g;
    this._labelBalance = bal.label;
    this.balanceText.anchor.set(0, 0);
    this._balanceCardX = inset;
    this._balanceCardY = top;
    this._balanceCardW = cardW;
    this._balanceCardH = cardH;

    this._currencyBadge = new Text({
      text: '',
      style: {
        fontFamily: this.t.fontUi,
        fontSize: 9,
        fontWeight: '700',
        fill: 0x64748b,
        letterSpacing: 1.8,
      },
    });
    this._currencyBadge.eventMode = 'none';
    this._currencyBadge.visible = false;
    this.root.addChild(this._currencyBadge);

    this.linesText = new Text({
      text: '',
      style: {
        fontFamily: this.t.fontUi,
        fontSize: this.u.typeStatValue,
        fontWeight: '700',
        fill: this.t.goldBright,
      },
    });
    const linesX = inset + cardW + gap;
    const lines = this._statCard(linesX, top, cardW, cardH, this._txtUpper('LINES'), this.linesText);
    this._cardLinesG = lines.g;
    this._linesStatLabel = lines.label;
    this._linesCardX = linesX;
    this._linesCardW = cardW;

    this.betText = new Text({
      text: '',
      style: {
        fontFamily: this.t.fontUi,
        fontSize: this.u.typeStatValue,
        fontWeight: '700',
        fill: this.t.goldBright,
      },
    });
    const betX = linesX + cardW + gap;
    const bet = this._statCard(betX, top, cardW, cardH, this._txtUpper('TOTAL_BET'), this.betText);
    this._cardBetG = bet.g;
    this._betStatLabel = bet.label;
    this._betCardX = betX;
    this._betCardW = cardW;

    this._labelTotalBet = null;
    this.totalBetText = null;
    this._linesCardY = top;
    this._linesCardH = cardH;

    this.winText = new Text({
      text: '',
      style: {
        fontFamily: this.t.fontUi,
        fontSize: this.u.typeWinLastTotal,
        fontWeight: '800',
        fill: 0xffffff,
        letterSpacing: 0.5,
        dropShadow: {
          alpha: 0.45,
          angle: Math.PI / 2,
          blur: 3,
          color: 0x000000,
          distance: 1,
        },
      },
    });
    const winX = betX + cardW + gap;
    const winW = cardW;
    const win = this._statCard(winX, top, winW, cardH, this._txtUpper('LAST_WIN'), this.winText);
    this._cardWinG = win.g;
    this._labelWin = win.label;
    this.winText.anchor.set(0, 0.5);
    this._winCard = { x: winX, y: top, w: winW, h: cardH };

    this.winDetailText = new Text({
      text: '',
      style: {
        fontFamily: this.t.fontUi,
        fontSize: this.u.typeWinDetail,
        fontWeight: '600',
        fill: this.t.textMuted,
        letterSpacing: 0.3,
        lineHeight: Math.round(this.u.typeWinDetail * 1.4),
        wordWrap: true,
        wordWrapWidth: Math.max(40, winW - 2 * this.u.cardPadX),
      },
    });
    this.winDetailText.anchor.set(0, 1);
    this.winDetailText.eventMode = 'none';
    this.root.addChild(this.winDetailText);

    this._refreshTexts();
  }

  _layoutWinCardTexts() {
    if (!this._winCard || !this.winDetailText || !this._labelWin) return;
    const { x, y, w, h } = this._winCard;
    const mul = this._uiMul || 1;
    const px = Math.round(this.u.cardPadX * mul);
    const py = Math.round(this.u.cardPadY * mul);
    const bottom = y + h - py;
    const labelReserve = this.u.typeStatLabel + this.u.cardPadY + 10;

    const labelBottom = this._labelWin.y + this._labelWin.height;
    const bandH = Math.max(0, bottom - labelBottom);
    const biasRaw = this.u.lastWinVerticalBias ?? 0.68;
    const bias = Math.min(0.9, Math.max(0.4, biasRaw));
    const midY = labelBottom + bandH * bias;
    this.winText.anchor.set(0, 0.5);
    this.winText.x = x + px;
    this.winText.y = midY;

    this.winDetailText.style.wordWrapWidth = Math.max(40, w - 2 * px);
    this.winDetailText.x = x + px;

    if (this.winValue > 0 && this._winHits.length > 0 && this.winDetailText.text) {
      const gap = 8;
      const amountTop = this.winText.y - this.winText.height / 2;
      let detailY = amountTop - gap;
      this.winDetailText.y = detailY;
      const detailTop = detailY - this.winDetailText.height;
      const minY = y + labelReserve;
      if (detailTop < minY) {
        const overflow = minY - detailTop;
        this.winDetailText.y += overflow;
        const maxDetailBottom = amountTop - 2;
        if (this.winDetailText.y > maxDetailBottom) {
          this.winDetailText.y = maxDetailBottom;
        }
      }
    } else {
      this.winDetailText.y = bottom;
    }
  }

  _layoutBetControlsRow() {
    const mul = this._uiMul || 1;
    const baseChipW = this.u.betChipW;
    const baseChipH = this.u.betChipH;
    const bh = baseChipH * mul;
    const chipGap = this.u.betChipGap * mul;
    const px = this.u.cardPadX * mul;
    const py = this.u.cardPadY * mul;
    const vGap = this.u.betLabelChipGap * mul;
    const cardTop = this._betCardY ?? this.u.panelPadY;
    const cardH = this._betCardH ?? this.u.statRowH;
    const innerL = this._betCardX + px;
    const innerR = this._betCardX + this._betCardW - px;
    const innerW = innerR - innerL;
    const innerBottom = cardTop + cardH - py;

    const labelBottom = this._betStatLabel.y + this._betStatLabel.height;
    const minChipY = labelBottom + vGap;
    let chipY = innerBottom - bh;
    if (chipY < minChipY) chipY = minChipY;

    const gapTotal = 2 * chipGap;
    const chipMin = this.u.betChipMinW * mul;
    const sideRatio = this.u.betChipSideRatio;
    const minMidTarget = this.u.betInputMinW * mul;
    const minMid = Math.min(
      minMidTarget,
      Math.max(16, innerW - gapTotal - chipMin * 2),
    );

    let sideW = Math.max(chipMin, Math.floor(innerW * sideRatio));
    let middleW = innerW - 2 * sideW - gapTotal;
    if (middleW < minMid) {
      sideW = Math.max(16, Math.floor((innerW - gapTotal - minMid) / 2));
      middleW = innerW - 2 * sideW - gapTotal;
    }
    if (middleW < 8) {
      sideW = Math.max(12, Math.floor((innerW - gapTotal - 8) / 2));
      middleW = Math.max(8, innerW - 2 * sideW - gapTotal);
    }

    const startX = innerL;
    const textX = startX + sideW + chipGap;
    const sx = sideW / baseChipW;
    const s = Math.min(sx, mul);

    this._betChipMinus.scale.set(s);
    this._betChipPlus.scale.set(s);
    this._betChipMinus._layoutScaleX = s;
    this._betChipPlus._layoutScaleX = s;

    /* Center bet value text (now a Pixi display, not HTML overlay) */
    this.betText.anchor.set(0.5, 0.5);
    this.betText.x = textX + middleW / 2;
    this.betText.y = chipY + bh / 2;

    this._betChipMinus.x = startX;
    this._betChipMinus.y = chipY + (bh - baseChipH * s) / 2;
    this._betChipPlus.x = textX + middleW + chipGap;
    this._betChipPlus.y = chipY + (bh - baseChipH * s) / 2;

    this._betInputRect = { x: textX, y: chipY, w: middleW, h: bh };
  }

  _layoutLinesControlsRow() {
    const mul = this._uiMul || 1;
    const baseChipW = this.u.betChipW;
    const baseChipH = this.u.betChipH;
    const bh = baseChipH * mul;
    const chipGap = this.u.betChipGap * mul;
    const px = this.u.cardPadX * mul;
    const py = this.u.cardPadY * mul;
    const vGap = this.u.betLabelChipGap * mul;
    const cardTop = this._linesCardY ?? this.u.panelPadY;
    const cardH = this._linesCardH ?? this.u.statRowH;
    const innerL = this._linesCardX + px;
    const innerR = this._linesCardX + this._linesCardW - px;
    const innerW = innerR - innerL;
    const innerBottom = cardTop + cardH - py;

    const labelBottom = this._linesStatLabel.y + this._linesStatLabel.height;
    const minChipY = labelBottom + vGap;
    let chipY = innerBottom - bh;
    if (chipY < minChipY) chipY = minChipY;

    const gapTotal = 2 * chipGap;
    const chipMin = this.u.betChipMinW * mul;
    const sideRatio = this.u.betChipSideRatio;
    const minMidTarget = this.u.betInputMinW * mul;
    const minMid = Math.min(
      minMidTarget,
      Math.max(16, innerW - gapTotal - chipMin * 2),
    );

    let sideW = Math.max(chipMin, Math.floor(innerW * sideRatio));
    let middleW = innerW - 2 * sideW - gapTotal;
    if (middleW < minMid) {
      sideW = Math.max(16, Math.floor((innerW - gapTotal - minMid) / 2));
      middleW = innerW - 2 * sideW - gapTotal;
    }
    if (middleW < 8) {
      sideW = Math.max(12, Math.floor((innerW - gapTotal - 8) / 2));
      middleW = Math.max(8, innerW - 2 * sideW - gapTotal);
    }

    const startX = innerL;
    const textX = startX + sideW + chipGap;
    const sx = sideW / baseChipW;
    const s = Math.min(sx, mul);

    this._linesChipMinus.scale.set(s);
    this._linesChipPlus.scale.set(s);
    this._linesChipMinus._layoutScaleX = s;
    this._linesChipPlus._layoutScaleX = s;

    const th = this.linesText.height;
    const baselineY = chipY + bh / 2 + th / 2;

    this.linesText.anchor.set(0.5, 1);
    this.linesText.x = textX + middleW / 2;
    this.linesText.y = baselineY;

    this._linesChipMinus.x = startX;
    this._linesChipMinus.y = chipY + (bh - baseChipH * s) / 2;
    this._linesChipPlus.x = textX + middleW + chipGap;
    this._linesChipPlus.y = chipY + (bh - baseChipH * s) / 2;

    this._linesInputRect = { x: textX, y: chipY, w: middleW, h: bh };
  }

  _layoutAllBetLikeRows() {
    if (this._layoutMode === 'mobile-portrait') {
      /* Preset chips handle bet selection; +/- controls are hidden */
      this._linesInputRect = { x: 0, y: 0, w: 0, h: 0 };
      this.onBetLayoutChanged?.();
      return;
    }
    this._layoutBetControlsRow();
    this._linesInputRect = { x: 0, y: 0, w: 0, h: 0 };
    this.onBetLayoutChanged?.();
  }

  _hideLinesUi() {
    if (this._cardLinesG) this._cardLinesG.visible = false;
    if (this._linesStatLabel) this._linesStatLabel.visible = false;
    if (this.linesText) this.linesText.visible = false;
    if (this._linesChipMinus) {
      this._linesChipMinus.visible = false;
      this._linesChipMinus.eventMode = 'none';
    }
    if (this._linesChipPlus) {
      this._linesChipPlus.visible = false;
      this._linesChipPlus.eventMode = 'none';
    }
  }

  /** Bet value field in UILayer root coordinates (for HTML overlay). */
  getBetInputRect() {
    return this._betInputRect
      ? { ...this._betInputRect }
      : { x: 0, y: 0, w: 0, h: 0 };
  }

  /** Lines count field in UILayer root coordinates (for HTML overlay). */
  getLinesInputRect() {
    return this._linesInputRect
      ? { ...this._linesInputRect }
      : { x: 0, y: 0, w: 0, h: 0 };
  }

  /** One-time setup of the scrollable chip group used in mobile portrait. */
  _buildChipsScrollGroup() {
    this._chipsScrollGroupMask = new Graphics();
    this._chipsScrollGroupMask.eventMode = 'none';
    this.root.addChild(this._chipsScrollGroupMask);

    this._chipsScrollGroup = new Container();
    this._chipsScrollGroup.mask = this._chipsScrollGroupMask;
    this._chipsScrollGroup.eventMode = 'static';
    this._chipsScrollGroup.visible = false;
    this.root.addChild(this._chipsScrollGroup);

    this._chipsScrollGroup.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._chipScrollDragging = true;
      this._chipScrollDidDrag = false;
      this._chipScrollStartGlobalX = e.global?.x ?? e.globalX ?? 0;
      this._chipScrollStartScrollX = this._chipScrollX;
    });
    this._chipsScrollGroup.on('pointermove', (e) => {
      if (!this._chipScrollDragging) return;
      const dx = (e.global?.x ?? e.globalX ?? 0) - this._chipScrollStartGlobalX;
      if (Math.abs(dx) > 8) this._chipScrollDidDrag = true;
      const next = Math.max(this._chipScrollMaxX, Math.min(0, this._chipScrollStartScrollX + dx));
      if (next !== this._chipScrollX) {
        this._chipScrollX = next;
        this._applyChipScroll();
      }
    });
    const endDrag = () => {
      this._chipScrollDragging = false;
      /* Keep _chipScrollDidDrag true briefly so chipTap handlers can read it. */
      setTimeout(() => { this._chipScrollDidDrag = false; }, 80);
    };
    this._chipsScrollGroup.on('pointerup', endDrag);
    this._chipsScrollGroup.on('pointerupoutside', endDrag);
  }

  _applyChipScroll() {
    const chipGap = Math.round(6 * (this._uiMul || 1));
    const chipW = this._chipItemW;
    this._presetChipContainers.forEach((c, i) => {
      c.x = i * (chipW + chipGap) + this._chipScrollX;
    });
  }

  _buildLinesControls() {
    const bw = this.u.betChipW;
    const bh = this.u.betChipH;
    const rr = this.u.radiusChip;

    const mkBtn = (label, x, y, onPress) => {
      const g = new Graphics();
      const grad = new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 1 },
        textureSpace: 'local',
        colorStops: [
          { offset: 0, color: this.t.chipFaceHi },
          { offset: 0.42, color: this.t.chipFaceMid },
          { offset: 1, color: this.t.chipFaceLo },
        ],
      });
      g.roundRect(0, 0, bw, bh, rr).fill(grad);
      g.roundRect(0, 0, bw, bh, rr).stroke({
        width: this.u.strokeChip,
        color: this.t.gold,
        alpha: 0.85,
      });
      g.roundRect(2, 2, bw - 4, bh - 4, Math.max(0, rr - 2)).stroke({
        width: this.u.strokeChipInner,
        color: this.t.goldBright,
        alpha: 0.38,
      });
      const t = new Text({
        text: label,
        style: {
          fontFamily: this.t.fontUi,
          fontSize: this.u.typeBetGlyph,
          fontWeight: '800',
          fill: this.t.lightning,
          dropShadow: {
            alpha: 0.75,
            angle: Math.PI / 2,
            blur: 3,
            color: 0x1a0a28,
            distance: 1,
          },
        },
      });
      t.anchor.set(0.5);
      t.x = bw / 2;
      t.y = bh / 2;
      g.eventMode = 'none';
      t.eventMode = 'none';
      const c = new Container();
      c.addChild(g, t);
      c.x = x;
      c.y = y;
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.hitArea = new Rectangle(0, 0, bw, bh);
      c.on('pointerdown', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        onPress();
        const sx = c._layoutScaleX ?? 1;
        this.animationController.runGsap(c.scale, {
          duration: 0.12,
          x: sx * 0.95,
          y: 0.95,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        });
      });
      this.root.addChild(c);
      return c;
    };

    this._linesChipMinus = mkBtn('−', 0, 0, () => this.adjustLines(-1));
    this._linesChipPlus = mkBtn('+', 0, 0, () => this.adjustLines(1));
  }

  _buildBetControls() {
    const bw = this.u.betChipW;
    const bh = this.u.betChipH;
    const rr = this.u.radiusChip;

    const mkBtn = (label, x, y, onPress) => {
      const g = new Graphics();
      const grad = new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 1 },
        textureSpace: 'local',
        colorStops: [
          { offset: 0, color: this.t.chipFaceHi },
          { offset: 0.42, color: this.t.chipFaceMid },
          { offset: 1, color: this.t.chipFaceLo },
        ],
      });
      g.roundRect(0, 0, bw, bh, rr).fill(grad);
      g.roundRect(0, 0, bw, bh, rr).stroke({
        width: this.u.strokeChip,
        color: this.t.gold,
        alpha: 0.85,
      });
      g.roundRect(2, 2, bw - 4, bh - 4, Math.max(0, rr - 2)).stroke({
        width: this.u.strokeChipInner,
        color: this.t.goldBright,
        alpha: 0.38,
      });
      /* Shadow drop for depth */
      g.roundRect(4, 4, bw - 8, bh - 8, Math.max(0, rr - 4)).stroke({
        width: 1,
        color: 0x000000,
        alpha: 0.18,
      });
      const t = new Text({
        text: label,
        style: {
          fontFamily: this.t.fontUi,
          /* Slightly smaller so glyphs don't clip; centered by Pixi bounds */
          fontSize: Math.round(this.u.typeBetGlyph * 0.88),
          fontWeight: '800',
          fill: this.t.lightning,
          dropShadow: {
            alpha: 0.65,
            angle: Math.PI / 2,
            blur: 2,
            color: 0x000000,
            distance: 1,
          },
        },
      });
      /* anchor (0.5, 0.44) compensates for ascender space in most fonts */
      t.anchor.set(0.5, 0.44);
      t.x = Math.round(bw / 2);
      t.y = Math.round(bh / 2);
      g.eventMode = 'none';
      t.eventMode = 'none';
      const c = new Container();
      c.addChild(g, t);
      c.x = x;
      c.y = y;
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.hitArea = new Rectangle(0, 0, bw, bh);
      c.on('pointerdown', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        onPress();
        const sx = c._layoutScaleX ?? 1;
        this.animationController.runGsap(c.scale, {
          duration: 0.1,
          x: sx * 0.93,
          y: 0.93,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        });
      });
      this.root.addChild(c);
      return c;
    };

    this._betChipMinus = mkBtn('−', 0, 0, () => this.adjustBet(-1));
    this._betChipPlus = mkBtn('+', 0, 0, () => this.adjustBet(1));
  }

  _buildSpinButton() {
    const w = this.u.spinW;
    const h = this.u.spinH;
    const inset = this.u.panelPadX;
    const statTop = this.u.panelPadY;
    const px = this.panelWidth - w - inset;
    const py = statTop + (this.u.statRowH - h) / 2;

    const g = new Graphics();

    const t = new Text({
      text: this._txtUpper('SPIN'),
      style: {
        fontFamily: this.t.fontDisplay,
        fontSize: this.u.typeSpin,
        fontWeight: '700',
        fill: 0xfffef8,
        letterSpacing: 10,
        dropShadow: {
          alpha: 0.92,
          angle: Math.PI / 2,
          blur: 5,
          color: 0x1a0538,
          distance: 2,
        },
      },
    });
    t.anchor.set(0.5);
    g.eventMode = 'none';
    t.eventMode = 'none';

    this.spinButton = new Container();
    this.spinButton.addChild(g, t);
    this.spinButton.x = px;
    this.spinButton.y = py;
    this.spinButton.eventMode = 'static';
    this.spinButton.cursor = 'pointer';
    this.spinButton.hitArea = new Rectangle(0, 0, w, h);
    this._spinBg = g;
    this._spinLabel = t;
    this._repaintSpinButton();

    this.spinAdapter = createSpineAdapter(this.spinButton, {
      animationController: this.animationController,
      idleMode: 'static',
    });

    this.spinButton.on('pointerover', () => {
      if (!this._spinEnabled) return;
      this.animationController.runGsap(this.spinButton.scale, {
        x: this._spinBaseScale * 1.03,
        y: this._spinBaseScale * 1.03,
        duration: 0.16,
        ease: 'power2.out',
      });
    });
    this.spinButton.on('pointerout', () => {
      this.animationController.runGsap(this.spinButton.scale, {
        x: this._spinBaseScale,
        y: this._spinBaseScale,
        duration: 0.16,
        ease: 'power2.out',
      });
    });
    this.spinButton.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (!this._spinEnabled) return;
      this.spinAdapter.play(gameConfig.animations.press);
    });
    const doSpin = (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (!this._spinEnabled) return;
      this.spinAdapter.play(gameConfig.animations.release);
      this.onSpin();
    };
    this.spinButton.on('pointertap', doSpin);
    this.spinButton.on('pointerupoutside', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (!this._spinEnabled) return;
      this.spinAdapter.play(gameConfig.animations.release);
    });

    this.root.addChild(this.spinButton);
  }

  /* ─── Panel action buttons (AUTO, MAX, SETTINGS) ──────────────────────── */

  /**
   * Shared pill button factory used by AUTO, MAX, and settings buttons.
   */
  _makePillBtn(label, btnW, btnH, rr, onTap) {
    const g = new Graphics();
    this._paintPillBg(g, btnW, btnH, rr, false);

    const t = new Text({
      text: label,
      style: {
        fontFamily: this.t.fontUi,
        fontSize: Math.round(btnH * 0.34),
        fontWeight: '800',
        fill: this.t.textBright,
        letterSpacing: 1.8,
      },
    });
    t.anchor.set(0.5, 0.44);
    t.x = Math.round(btnW / 2);
    t.y = Math.round(btnH / 2);
    t.eventMode = 'none';
    g.eventMode = 'none';

    const c = new Container();
    c.addChild(g, t);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(0, 0, btnW, btnH);
    c.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this.animationController.runGsap(c.scale, {
        duration: 0.1,
        x: (c.scale.x || 1) * 0.92,
        y: (c.scale.y || 1) * 0.92,
        yoyo: true,
        repeat: 1,
        ease: 'power2.inOut',
      });
    });
    c.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      onTap();
    });
    this.root.addChild(c);
    return { container: c, bg: g, label: t };
  }

  _paintPillBg(g, w, h, rr, active) {
    g.clear();
    const grad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: active
        ? [
            { offset: 0, color: this.t.spinCoreHi },
            { offset: 0.5, color: this.t.spinCore },
            { offset: 1, color: this.t.spinCoreLo },
          ]
        : [
            { offset: 0, color: this.t.chipFaceHi },
            { offset: 0.45, color: this.t.chipFaceMid },
            { offset: 1, color: this.t.chipFaceLo },
          ],
    });
    g.roundRect(0, 0, w, h, rr).fill(grad);
    g.roundRect(0, 0, w, h, rr).stroke({
      width: 1.5,
      color: active ? this.t.spinRing : this.t.gold,
      alpha: active ? 0.95 : 0.75,
    });
    g.roundRect(2, 2, w - 4, h - 4, Math.max(0, rr - 2)).stroke({
      width: 1,
      color: 0xffffff,
      alpha: active ? 0.22 : 0.08,
    });
  }

  _buildAutoplayBtn() {
    const W = 80, H = 44, RR = 22;
    const b = this._makePillBtn(this._txtUpper('AUTO'), W, H, RR, () => {
      this._autoplaying = !this._autoplaying;
      this._refreshAutoBtn();
      this.onAutoplayToggle?.(this._autoplaying);
    });
    this._autoBtn = b.container;
    this._autoBg = b.bg;
    this._autoLabel = b.label;
    this._autoBtnW = W;
    this._autoBtnH = H;
    this._autoBtnRr = RR;
  }

  _refreshAutoBtn() {
    if (!this._autoBg || !this._autoLabel) return;
    this._paintPillBg(this._autoBg, this._autoBtnW, this._autoBtnH, this._autoBtnRr, this._autoplaying);
    this._autoLabel.style.fill = this._autoplaying ? 0xfffef8 : this.t.textBright;
    this._autoLabel.style.dropShadow = this._autoplaying
      ? { alpha: 0.7, angle: Math.PI / 2, blur: 4, color: this.t.spinCoreLo, distance: 0 }
      : null;
  }

  setAutoplayActive(on) {
    this._autoplaying = !!on;
    this._refreshAutoBtn();
    this._refreshAutoCircleBtn();
  }

  _buildMaxBetBtn() {
    const W = 80, H = 44, RR = 22;
    const b = this._makePillBtn(this._txtUpper('MAX'), W, H, RR, () => {
      this.onMaxBet?.();
    });
    this._maxBetBtn = b.container;
    this._maxBetBg = b.bg;
    this._maxBtnW = W;
    this._maxBtnH = H;
    this._maxBtnRr = RR;
  }

  _buildSettingsBtn() {
    const W = 44, H = 44, RR = 14;
    const g = new Graphics();
    this._paintPillBg(g, W, H, RR, false);

    /* Gear icon drawn with Pixi Graphics */
    const ic = new Graphics();
    const cx = W / 2, cy = H / 2, ir = 5.5, or = 10, teeth = 8;
    ic.circle(cx, cy, ir).fill({ color: this.t.textBright, alpha: 0.9 });
    ic.circle(cx, cy, ir - 1.5).fill({ color: this.t.chipFaceMid, alpha: 1 });
    for (let i = 0; i < teeth; i++) {
      const a0 = (i / teeth) * Math.PI * 2;
      const a1 = ((i + 0.35) / teeth) * Math.PI * 2;
      const a2 = ((i + 0.65) / teeth) * Math.PI * 2;
      const a3 = ((i + 1) / teeth) * Math.PI * 2;
      ic.moveTo(cx + Math.cos(a0) * ir, cy + Math.sin(a0) * ir);
      ic.lineTo(cx + Math.cos(a1) * or, cy + Math.sin(a1) * or);
      ic.lineTo(cx + Math.cos(a2) * or, cy + Math.sin(a2) * or);
      ic.lineTo(cx + Math.cos(a3) * ir, cy + Math.sin(a3) * ir);
      ic.closePath();
    }
    ic.fill({ color: this.t.textBright, alpha: 0.85 });
    ic.eventMode = 'none';
    g.eventMode = 'none';

    const c = new Container();
    c.addChild(g, ic);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(0, 0, W, H);
    c.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this.animationController.runGsap(c.scale, {
        duration: 0.1,
        x: (c.scale.x || 1) * 0.9,
        y: (c.scale.y || 1) * 0.9,
        yoyo: true,
        repeat: 1,
        ease: 'power2.inOut',
      });
    });
    c.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this.onSettingsToggle?.();
    });
    this.root.addChild(c);
    this._settingsIconBtn = c;
    this._settingsIconBg = g;
    this._settingsBtnW = W;
    this._settingsBtnH = H;
  }

  _repaintSpinButton() {
    if (!this._spinBg || !this._spinLabel) return;
    const mul = this._uiMul || 1;
    const g = this._spinBg;
    const t = this._spinLabel;
    const mobilePortrait = this._layoutMode === 'mobile-portrait';
    g.clear();
    if (mobilePortrait) {
      const d = Math.round(110 * mul);
      const r = d / 2;
      this._spinVisualW = d;
      this._spinVisualH = d;
      const outerGrad = new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 1 },
        textureSpace: 'local',
        colorStops: [
          { offset: 0, color: this.t.lightning },
          { offset: 0.1, color: 0xf0f9ff },
          { offset: 0.38, color: this.t.spinCoreHi },
          { offset: 0.78, color: this.t.spinCore },
          { offset: 1, color: this.t.spinCoreLo },
        ],
      });
      /* Outer ring shadow */
      g.circle(r, r + 3, r).fill({ color: 0x000000, alpha: 0.32 });
      g.circle(r, r, r).fill(outerGrad);
      g.circle(r, r, r - 1.5 * mul).stroke({ width: 4 * mul, color: this.t.spinRing, alpha: 1 });
      g.circle(r, r, r - 7 * mul).stroke({ width: 1.5 * mul, color: 0xffffff, alpha: 0.38 });
      g.circle(r, r, r - 10 * mul).stroke({ width: 1 * mul, color: 0x000000, alpha: 0.22 });
      t.style.fontSize = Math.round(Math.max(24, this.u.typeSpin) * mul);
      t.style.letterSpacing = 5 * mul;
      t.x = r;
      t.y = r + 1;
      this.spinButton.hitArea = new Rectangle(0, 0, d, d);
    } else {
      const w = Math.round(this.u.spinW * mul);
      const h = Math.round(this.u.spinH * mul);
      const rr = this.u.radiusSpin * mul;
      this._spinVisualW = w;
      this._spinVisualH = h;
      const fillGrad = new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 1 },
        textureSpace: 'local',
        colorStops: [
          { offset: 0, color: this.t.lightning },
          { offset: 0.1, color: this.t.spinCoreHi },
          { offset: 0.45, color: this.t.spinCore },
          { offset: 1, color: this.t.spinCoreLo },
        ],
      });
      g.roundRect(0, 0, w, h, rr).fill(fillGrad);
      g.roundRect(0, 0, w, h, rr).stroke({
        width: this.u.strokeSpinOuter,
        color: this.t.spinRing,
        alpha: 1,
      });
      g.roundRect(3 * mul, 3 * mul, w - 6 * mul, h - 6 * mul, Math.max(0, rr - 3 * mul)).stroke({
        width: this.u.strokeSpinInner,
        color: 0xffffff,
        alpha: 0.38,
      });
      g.roundRect(5 * mul, 5 * mul, w - 10 * mul, h - 10 * mul, Math.max(0, rr - 5 * mul)).stroke({
        width: 1,
        color: 0x000000,
        alpha: 0.28,
      });
      t.style.fontSize = Math.round(this.u.typeSpin * mul);
      t.style.letterSpacing = 10 * mul;
      t.x = w / 2;
      t.y = h / 2;
      this.spinButton.hitArea = new Rectangle(0, 0, w, h);
    }
  }

  _txt(key) {
    return this.translate(key);
  }

  _txtUpper(key) {
    return this._txt(key).toLocaleUpperCase();
  }

  _twoLineText(key) {
    const text = this._txtUpper(key).replace(/\s+/g, ' ').trim();
    const words = text.split(' ').filter(Boolean);
    if (words.length <= 2) return text;
    const splitAt = Math.ceil(words.length / 2);
    return `${words.slice(0, splitAt).join(' ')}\n${words.slice(splitAt).join(' ')}`;
  }

  _currencyLabel() {
    return formatCurrencyRulesLabel(this.currencyCode, this.currencySymbol) || 'FUN';
  }

  _formatDigits(value) {
    return formatCurrencyDigits(Number(value) || 0, this.currencyPrecision);
  }

  setTranslator(translate) {
    if (typeof translate === 'function') this.translate = translate;
    if (this._labelBalance) this._labelBalance.text = this._txtUpper('BALANCE');
    if (this._linesStatLabel) this._linesStatLabel.text = this._txtUpper('LINES');
    if (this._labelTotalBet) this._labelTotalBet.text = this._txtUpper('TOTAL_BET');
    if (this._betStatLabel) this._betStatLabel.text = this._txtUpper('TOTAL_BET');
    if (this._labelWin) this._labelWin.text = this._txtUpper('LAST_WIN');
    if (this._spinLabel) this._spinLabel.text = this._txtUpper('SPIN');
    this._refreshTexts();
  }

  setCurrency({ currencyCode, currencyPrecision, currencySymbol } = {}) {
    if (currencyCode) this.currencyCode = currencyCode;
    if (Number.isFinite(Number(currencyPrecision))) this.currencyPrecision = Number(currencyPrecision);
    this.currencySymbol =
      currencySymbol == null || String(currencySymbol).trim() === ''
        ? undefined
        : String(currencySymbol).trim();
    this._refreshTexts();
  }

  _refreshTexts() {
    this.balanceText.text = Number.isFinite(this.balanceValue)
      ? this._formatDigits(this.balanceValue)
      : '—';
    this.betText.text  = this._formatDigits(this.betValue);
    this.linesText.text = String(this.linesValue);
    if (this.totalBetText) this.totalBetText.text = this._formatDigits(this.betValue);
    this.winText.text = this.winValue > 0 ? this._formatDigits(this.winValue) : '—';
    this.winText.style.fill = this.winValue > 0 ? 0xffffff : this.t.textMuted;

    /** LAST WIN: total only — no per-line breakdown */
    this.winDetailText.text = '';

    /* Info text shown in mobile portrait centre column */
    if (this._infoText) {
      this._infoText.text = this.winValue > 0
        ? `${this._txtUpper('WIN')}\n${this._formatDigits(this.winValue)}`
        : this._twoLineText('PLEASE_PLACE_YOUR_BET');
    }
    /* Currency badge under balance */
    if (this._currencyBadge) this._currencyBadge.text = this._currencyLabel();

    /* Highlight selected preset chip */
    this._refreshPresetChips();

    if (this._betChipMinus) this._layoutAllBetLikeRows();
    this._layoutInlineBalanceAndTotalBet();
    this._layoutWinCardTexts();
    this._syncChromeShadows(this._uiMul || 1);
  }

  setSpinEnabled(on) {
    this._spinEnabled = on;
    this.spinButton.alpha = on ? 1 : 0.5;
    this.spinButton.eventMode = on ? 'static' : 'none';
    this.spinButton.cursor = on ? 'pointer' : 'default';
  }

  setBalance(v) {
    this.balanceValue = v;
    this._refreshTexts();
  }

  setBet(v) {
    this.betValue = v;
    this._refreshTexts();
    this.onBetSync?.(this.betValue);
  }

  setLines(v) {
    const maxL = gameConfig.paylines.length;
    this.linesValue = Math.min(maxL, Math.max(1, Math.floor(v)));
    this._refreshTexts();
    this.onLinesSync?.(this.linesValue);
  }

  adjustLines(delta) {
    this.setLines(this.linesValue + delta);
  }

  /**
   * Updates LAST WIN only when {@link v} &gt; 0 — losing spins keep the previous amount visible.
   * @param {number} v Total won this round (must be &gt; 0 to apply)
   * @param {import('../logic/winEvaluator.js').WinResult['hits'] | null} [hits] Per-payline wins for this round
   */
  setWin(v, hits = null) {
    if (v <= 0) return;
    this.winValue = v;
    if (Array.isArray(hits)) this._winHits = hits;
    this._refreshTexts();
  }

  adjustBet(delta) {
    const step = Math.max(1, Math.floor(gameConfig.defaultBet / 2));
    const next = Math.min(
      gameConfig.maxBet,
      Math.max(gameConfig.minBet, this.betValue + delta * step),
    );
    this.setBet(next);
  }

  /* ═══════════════════════════════════════════════════════════════════
   *  Mobile-portrait specific elements
   * ═══════════════════════════════════════════════════════════════════ */

  /** "PLEASE PLACE YOUR BET" / win info centred between balance and last win. */
  _buildInfoText() {
    this._infoText = new Text({
      text: this._twoLineText('PLEASE_PLACE_YOUR_BET'),
      style: {
        fontFamily: this.t.fontUi,
        fontSize: 10,
        fontWeight: '700',
        fill: this.t.textLabel,
        letterSpacing: 0.3,
        align: 'center',
        wordWrap: false,
        lineHeight: 14,
      },
    });
    this._infoText.anchor.set(0.5, 0.5);
    this._infoText.eventMode = 'none';
    this._infoText.visible = false;
    this.root.addChild(this._infoText);
  }

  /** Small circular AUTO button used only in mobile portrait Row 3. */
  _buildAutoCircleBtn() {
    const D = 54, r = D / 2;
    const bg = new Graphics();
    bg.eventMode = 'none';

    /** Circular arrow icon */
    const ic = new Graphics();
    ic.eventMode = 'none';
    const ar = 10;
    const cx = r, cy = r;
    for (let seg = 0; seg < 24; seg++) {
      const a0 = (seg / 24) * Math.PI * 1.75 - Math.PI * 0.5;
      const a1 = ((seg + 1) / 24) * Math.PI * 1.75 - Math.PI * 0.5;
      const x0 = cx + Math.cos(a0) * ar, y0 = cy + Math.sin(a0) * ar;
      const x1 = cx + Math.cos(a1) * ar, y1 = cy + Math.sin(a1) * ar;
      ic.moveTo(x0, y0).lineTo(x1, y1);
    }
    ic.stroke({ width: 2.5, color: this.t.textBright, alpha: 0.9 });
    /* Arrowhead at end of arc */
    const aEnd = Math.PI * 1.75 - Math.PI * 0.5;
    const ax = cx + Math.cos(aEnd) * ar, ay = cy + Math.sin(aEnd) * ar;
    ic.moveTo(ax - 6 * Math.cos(aEnd - 0.8), ay - 6 * Math.sin(aEnd - 0.8));
    ic.lineTo(ax, ay);
    ic.lineTo(ax - 6 * Math.cos(aEnd + 0.8), ay - 6 * Math.sin(aEnd + 0.8));
    ic.stroke({ width: 2.5, color: this.t.textBright, alpha: 0.9 });

    const c = new Container();
    c.addChild(bg, ic);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(0, 0, D, D);
    c.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this.animationController.runGsap(c.scale, {
        duration: 0.1,
        x: (c.scale.x || 1) * 0.92,
        y: (c.scale.y || 1) * 0.92,
        yoyo: true, repeat: 1, ease: 'power2.inOut',
      });
    });
    c.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._autoplaying = !this._autoplaying;
      this._refreshAutoCircleBtn();
      this.onAutoplayToggle?.(this._autoplaying);
    });
    c.visible = false;
    this.root.addChild(c);
    this._autoCircleBtn   = c;
    this._autoCircleBg    = bg;
    this._autoCircleBtnD  = D;
    this._refreshAutoCircleBtn();
  }

  _refreshAutoCircleBtn() {
    const bg = this._autoCircleBg;
    if (!bg) return;
    const D = this._autoCircleBtnD ?? 54, r = D / 2;
    const active = this._autoplaying;
    bg.clear();
    const grad = new FillGradient({
      type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 }, textureSpace: 'local',
      colorStops: active
        ? [{ offset: 0, color: this.t.spinCoreHi }, { offset: 0.5, color: this.t.spinCore }, { offset: 1, color: this.t.spinCoreLo }]
        : [{ offset: 0, color: this.t.chipFaceHi }, { offset: 0.45, color: this.t.chipFaceMid }, { offset: 1, color: this.t.chipFaceLo }],
    });
    bg.circle(r, r + 2, r).fill({ color: 0x000000, alpha: 0.24 });
    bg.circle(r, r, r).fill(grad);
    bg.circle(r, r, r - 1.5).stroke({ width: 2.5, color: active ? this.t.spinRing : this.t.gold, alpha: active ? 1 : 0.82 });
    bg.circle(r, r, r - 6).stroke({ width: 1, color: 0xffffff, alpha: active ? 0.2 : 0.1 });
  }

  /** Small circular PAYTABLE / SETTINGS button used only in mobile portrait Row 3. */
  _buildPaytableBtn() {
    const D = 54, r = D / 2;
    const bg = new Graphics();
    bg.eventMode = 'none';
    this._paintPaytableBg(bg, D);

    /* Coin-stack icon */
    const ic = new Graphics();
    ic.eventMode = 'none';
    const cx = r, cy = r;
    const cw = 13, ch = 4.5;
    ic.ellipse(cx, cy + 7,  cw, ch).fill({ color: 0xd97706 });
    ic.ellipse(cx, cy + 3,  cw, ch).fill({ color: 0xf59e0b });
    ic.ellipse(cx, cy - 1,  cw, ch).fill({ color: 0xfbbf24 });
    ic.ellipse(cx, cy - 5,  cw, ch).fill({ color: 0xfcd34d });
    ic.ellipse(cx, cy - 5,  cw, ch).stroke({ width: 1, color: 0xfef3c7, alpha: 0.55 });

    const c = new Container();
    c.addChild(bg, ic);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(0, 0, D, D);

    /* Track movement to distinguish tap from drag. */
    let _tapStartX = 0, _tapStartY = 0;
    c.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      _tapStartX = e.global?.x ?? 0;
      _tapStartY = e.global?.y ?? 0;
      this.animationController.runGsap(c.scale, {
        duration: 0.1,
        x: (c.scale.x || 1) * 0.92,
        y: (c.scale.y || 1) * 0.92,
        yoyo: true, repeat: 1, ease: 'power2.inOut',
      });
    });
    c.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      const dx = (e.global?.x ?? 0) - _tapStartX;
      const dy = (e.global?.y ?? 0) - _tapStartY;
      if (Math.hypot(dx, dy) < 16) {
        this.onBetPopupToggle?.();
      }
    });
    c.visible = false;
    this.root.addChild(c);
    this._paytableBtn  = c;
    this._paytableBtnD = D;
  }

  _paintPaytableBg(g, D) {
    const r = D / 2;
    const grad = new FillGradient({
      type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 }, textureSpace: 'local',
      colorStops: [
        { offset: 0, color: this.t.chipFaceHi },
        { offset: 0.45, color: this.t.chipFaceMid },
        { offset: 1, color: this.t.chipFaceLo },
      ],
    });
    g.circle(r, r + 2, r).fill({ color: 0x000000, alpha: 0.24 });
    g.circle(r, r, r).fill(grad);
    g.circle(r, r, r - 1.5).stroke({ width: 2.5, color: this.t.gold, alpha: 0.82 });
    g.circle(r, r, r - 6).stroke({ width: 1, color: 0xffffff, alpha: 0.1 });
  }

  /* ── Preset bet chips ────────────────────────────────────────────── */

  /**
   * Build (or rebuild) preset bet chip buttons from the given bet amounts.
   * @param {number[]} bets
   */
  _buildPresetBetChips(bets) {
    for (const c of (this._presetChipContainers ?? [])) {
      if (c.parent) c.parent.removeChild(c);
      c.destroy({ children: true });
    }
    this._presetChipContainers = [];
    this._quickBets = Array.isArray(bets) ? [...bets] : [];

    for (const amount of this._quickBets) {
      const bw = 60, bh = this.u.betChipH, rr = this.u.radiusChip;

      const chipBg = new Graphics();
      chipBg.eventMode = 'none';
      this._paintPresetChipBg(chipBg, amount === this.betValue, bw, bh);

      const betLabel = new Text({
        text: this._txtUpper('BET'),
        style: {
          fontFamily: this.t.fontUi,
          fontSize: 9,
          fontWeight: '700',
          fill: this.t.textMuted,
          letterSpacing: 1.2,
        },
      });
      betLabel.anchor.set(0.5, 0);
      betLabel.x = bw / 2;
      betLabel.y = 5;
      betLabel.eventMode = 'none';

      const amtText = new Text({
        text: String(amount),
        style: {
          fontFamily: this.t.fontUi,
          fontSize: 15,
          fontWeight: '800',
          fill: this.t.textBright,
          letterSpacing: 0.3,
        },
      });
      amtText.anchor.set(0.5, 0.5);
      amtText.x = bw / 2;
      amtText.y = bh * 0.64;
      amtText.eventMode = 'none';

      const c = new Container();
      c.addChild(chipBg, betLabel, amtText);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.hitArea = new Rectangle(0, 0, bw, bh);
      c._betAmount = amount;
      c._chipBg    = chipBg;
      c._amtText   = amtText;
      c._betLabel  = betLabel;
      c.on('pointerdown', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this.animationController.runGsap(c.scale, {
          duration: 0.1,
          x: (c.scale.x || 1) * 0.93,
          y: (c.scale.y || 1) * 0.93,
          yoyo: true, repeat: 1, ease: 'power2.inOut',
        });
      });
      c.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        if (this._chipScrollDidDrag) return;
        this.setBet(amount);
      });
      c.visible = false;
      this._chipsScrollGroup.addChild(c);
      this._presetChipContainers.push(c);
    }
  }

  /**
   * Update chip amounts from backend quickBets and re-layout if in mobile portrait.
   * @param {number[]} bets
   */
  setQuickBets(bets) {
    this._buildPresetBetChips(bets);
    if (this._layoutMode === 'mobile-portrait') {
      this.setPanelWidth(this.panelWidth, true);
    }
  }

  /** Repaint chip backgrounds to reflect the currently selected bet. */
  _refreshPresetChips() {
    for (const c of (this._presetChipContainers ?? [])) {
      const sel = Math.abs((c._betAmount ?? -Infinity) - this.betValue) < 0.001;
      const bw  = c.hitArea?.width  ?? 60;
      const bh  = c.hitArea?.height ?? this.u.betChipH;
      this._paintPresetChipBg(c._chipBg, sel, bw, bh);
      if (c._amtText)  c._amtText.style.fill  = sel ? 0xffffff : this.t.textLabel;
      if (c._betLabel) c._betLabel.style.fill  = sel ? 0xd1fae5 : this.t.textMuted;
    }
    /* Scroll selected chip into view in mobile portrait. */
    if (this._layoutMode === 'mobile-portrait' && this._chipsScrollGroup?.visible) {
      this._scrollSelectedChipIntoView();
    }
  }

  _scrollSelectedChipIntoView() {
    const idx = this._presetChipContainers.findIndex(
      (c) => Math.abs((c._betAmount ?? -Infinity) - this.betValue) < 0.001,
    );
    if (idx < 0) return;
    const mul = this._uiMul || 1;
    const chipGapPx = Math.round(6 * mul);
    const chipW = this._chipItemW;
    const edge = Math.max(10 * mul, this.u.panelPadX - 10 * mul);
    const rowW = this.panelWidth - edge * 2;
    const itemLeft = idx * (chipW + chipGapPx);
    const itemRight = itemLeft + chipW;
    let scrollX = this._chipScrollX;
    if (itemLeft + scrollX < 0) scrollX = -itemLeft;
    else if (itemRight + scrollX > rowW) scrollX = rowW - itemRight;
    scrollX = Math.max(this._chipScrollMaxX, Math.min(0, scrollX));
    if (scrollX !== this._chipScrollX) {
      this._chipScrollX = scrollX;
      this._applyChipScroll();
    }
  }

  /**
   * Paint (or repaint) a preset chip's rounded-rect background.
   * @param {Graphics} g
   * @param {boolean}  selected
   * @param {number}   bw
   * @param {number}   bh
   */
  _paintPresetChipBg(g, selected, bw, bh) {
    if (!g) return;
    g.clear();
    const rr = this.u.radiusChip;
    const grad = new FillGradient({
      type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 }, textureSpace: 'local',
      colorStops: selected
        ? [
            { offset: 0,    color: 0x22c55e },
            { offset: 0.48, color: 0x16a34a },
            { offset: 1,    color: 0x14532d },
          ]
        : [
            { offset: 0,    color: this.t.chipFaceHi },
            { offset: 0.45, color: this.t.chipFaceMid },
            { offset: 1,    color: this.t.chipFaceLo },
          ],
    });
    g.roundRect(0, 0, bw, bh, rr).fill(grad);
    g.roundRect(0, 0, bw, bh, rr).stroke({
      width: 1.5,
      color: selected ? 0x4ade80 : this.t.gold,
      alpha: selected ? 1 : 0.7,
    });
    g.roundRect(2, 2, bw - 4, bh - 4, Math.max(0, rr - 2)).stroke({
      width: 1, color: 0xffffff, alpha: selected ? 0.22 : 0.07,
    });
  }
}
